import { notificationService } from '@/services/notificationService'
import type { RegistrationErrors, RegistrationStepTwoData, SelectOption } from '@/types'
import { ErrorHandler } from '@/utils/ErrorHandler'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import brandUserService from '../../services/brandUserService'
import useAccessStore from '../../store/accessStore'
import useBrandUserStore from '../../store/brandUserStore'
import { logError } from '../../utils/sentry'

import errorIcon from '../../assets/icons/ico_error_text.svg'
import inputStyles from '../../styles/input.module.css'
import styles from './registration.module.css'

interface RegistrationStepTwoProps {
  onPrev: () => void
}

export const RegistrationStepTwo = ({ onPrev }: RegistrationStepTwoProps) => {
  const { isAuthenticated, checkRegistrationStatus, registrationStatus } = useAccessStore()
  const { brandUser, error: storeErrors, registrationStepTwo, clearErrors, setValue } = useBrandUserStore()

  const jobPosition = brandUser?.jobPosition ?? null
  const phoneNumber = brandUser?.phoneNumber ?? null
  const companyName = brandUser?.companyName ?? null
  const region = brandUser?.region ?? null
  const industries = brandUser?.industries ?? null
  const annualBudget = brandUser?.annualBudget ?? null
  const companySize = brandUser?.companySize ?? null
  const additionalInformation = brandUser?.additionalInformation ?? null
  const id = brandUser?.id || ''
  const isLoading = false

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const hasPrefilledData =
    (jobPosition && jobPosition !== '') ||
    (phoneNumber && phoneNumber !== '') ||
    (companyName && companyName !== '') ||
    (region && region !== '') ||
    (industries && industries.length > 0) ||
    (annualBudget && annualBudget !== '') ||
    (companySize && companySize !== '') ||
    (additionalInformation && additionalInformation !== '')

  const [isPageRefresh] = useState(() => {
    const hasVisited = sessionStorage.getItem('registrationStep2Visited')
    if (!hasVisited) {
      sessionStorage.setItem('registrationStep2Visited', 'true')
      return false
    }
    return true
  })

  const [values, setValues] = useState<RegistrationStepTwoData & { userId?: string; brandLogoUrl?: string }>(() => {
    if (isPageRefresh || !hasPrefilledData) {
      return {
        jobPosition: '',
        phoneNumber: '',
        companyName: '',
        region: '',
        industries: [],
        annualBudget: '',
        companySize: '',
        additionalInformation: '',
        userId: id,
      }
    }

    return {
      jobPosition: jobPosition === null ? '' : jobPosition,
      phoneNumber: phoneNumber === null ? '' : phoneNumber,
      companyName: companyName === null ? '' : companyName,
      region: region === null ? '' : region,
      industries: industries === null ? [] : industries,
      annualBudget: annualBudget === null ? '' : annualBudget,
      companySize: companySize === null ? '' : companySize,
      additionalInformation: additionalInformation === null ? '' : additionalInformation,
      userId: id,
    }
  })

  const [errors, setErrors] = useState<RegistrationErrors>({
    jobPosition: '',
    phoneNumber: '',
    companyName: '',
    region: '',
    industries: '',
    annualBudget: '',
    companySize: '',
    additionalInformation: '',
    brandLogoUrl: '',
    general: '',
  })

  const [optionsData, setOptionsData] = useState<{
    regions: SelectOption[]
    industries: SelectOption[]
    companySizes: SelectOption[]
    budgets: SelectOption[]
  }>({
    regions: [],
    industries: [],
    companySizes: [],
    budgets: [],
  })

  const [optionsLoading, setOptionsLoading] = useState({
    regions: false,
    industries: false,
    companySizes: false,
    budgets: false,
  })

  const [authStatus, setAuthStatus] = useState({
    isChecking: true,
    isAuthenticated: !!isAuthenticated,
  })

  const navigate = useNavigate()

  useEffect(() => {
    const brandUserId = brandUser?.id
    const stateId = id
    const brandDataId = useAccessStore.getState().brandData?.id

    const userId = brandDataId || brandUserId || stateId

    if (!userId && isAuthenticated) {
      useAccessStore
        .getState()
        .fetchBrandData()
        .then((data) => {
          if (data?.id) {
            setValue('id', data.id)
            setValues((prev) => ({
              ...prev,
              userId: data.id,
            }))
          } else {
            logError('No userId available from API', { data })
          }
        })
        .catch((error) => {
          logError('Failed to fetch brand data for ID', { error })
        })
    } else if (userId && userId !== values.userId) {
      setValue('id', userId)
      setValues((prev) => ({
        ...prev,
        userId,
      }))
    } else if (!userId) {
      logError('No userId available from any source')
    }

    setAuthStatus({
      isChecking: false,
      isAuthenticated: !!isAuthenticated,
    })
  }, [isAuthenticated, setValue, brandUser?.id, id, values.userId])

  useEffect(() => {
    if (authStatus.isChecking || !authStatus.isAuthenticated) {
      return
    }

    const fetchOptions = async () => {
      try {
        setOptionsLoading((prev) => ({ ...prev, regions: true }))
        try {
          const regionsResponse = await api.get('/regions')

          if (regionsResponse.data?.items) {
            const regionOptions = regionsResponse.data.items.map((region: string) => ({
              value: region,
              label: region,
            }))
            setOptionsData((prev) => ({
              ...prev,
              regions: regionOptions,
            }))
          }
        } catch (error) {
          console.error('Failed to fetch regions:', error)
        }
        setOptionsLoading((prev) => ({ ...prev, regions: false }))

        setOptionsLoading((prev) => ({ ...prev, budgets: true }))
        try {
          const budgetsResponse = await api.get('/budgets')

          if (budgetsResponse.data?.items) {
            const budgetOptions = budgetsResponse.data.items.map((budget: string) => ({
              value: budget,
              label: budget,
            }))
            setOptionsData((prev) => ({
              ...prev,
              budgets: budgetOptions,
            }))
          }
        } catch (error) {
          console.error('Failed to fetch budgets:', error)
        }
        setOptionsLoading((prev) => ({ ...prev, budgets: false }))

        setOptionsLoading((prev) => ({ ...prev, companySizes: true }))
        try {
          const companySizesResponse = await api.get('/company_sizes')

          if (companySizesResponse.data?.items) {
            const companySizeOptions = companySizesResponse.data.items.map((size: string) => ({
              value: size,
              label: size,
            }))
            setOptionsData((prev) => ({
              ...prev,
              companySizes: companySizeOptions,
            }))
          }
        } catch (error) {
          console.error('Failed to fetch company sizes:', error)
        }
        setOptionsLoading((prev) => ({ ...prev, companySizes: false }))

        setOptionsLoading((prev) => ({ ...prev, industries: true }))
        try {
          const industriesResponse = await api.get('/industries')

          if (industriesResponse.data?.items) {
            const industryOptions = industriesResponse.data.items.map((industry: string) => ({
              value: industry,
              label: industry,
            }))
            setOptionsData((prev) => ({
              ...prev,
              industries: industryOptions,
            }))
          } else {
            console.error('Unexpected industries data format:', industriesResponse.data)
          }
        } catch (error) {
          console.error('Failed to fetch industries:', error)
        }
        setOptionsLoading((prev) => ({ ...prev, industries: false }))
      } catch (error) {
        console.error('General error in fetchOptions:', error)
        setOptionsLoading({
          regions: false,
          industries: false,
          companySizes: false,
          budgets: false,
        })
      }
    }

    fetchOptions()
  }, [authStatus])

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const MAX_FILE_SIZE = 104857600

    if (file.size > MAX_FILE_SIZE) {
      notificationService.error('File exceeds the maximum size limit of 100MB')
      setErrors((prev) => ({
        ...prev,
        brandLogoUrl: 'File size must be less than 100MB.',
      }))
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    setSelectedFile(file)
    setIsUploading(true)
    setErrors((prev) => ({ ...prev, brandLogoUrl: '' }))

    try {
      const logoUrl = await brandUserService.uploadBrandLogo(file)

      setValues((prev) => ({
        ...prev,
        brandLogoUrl: logoUrl,
      }))

      setValue('brandLogoUrl', logoUrl)
      notificationService.success('Logo uploaded successfully!')
    } catch (error) {
      console.error('Error uploading logo:', error)
      setErrors((prev) => ({
        ...prev,
        brandLogoUrl: 'Failed to upload logo. Please try again.',
      }))
      notificationService.error('Failed to upload logo. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target

    let processedValue = value

    if (id !== 'phoneNumber' && id !== 'region' && id !== 'industries' && id !== 'annualBudget' && id !== 'companySize') {
      processedValue = value.replace(/[а-яА-ЯёЁ]/g, '')
    }

    if (id === 'phoneNumber') {
      const digitsOnly = value.replace(/\D/g, '')

      if (digitsOnly === '') {
        processedValue = ''
      } else {
        const limitedDigits = digitsOnly.slice(0, 14)
        processedValue = `+${limitedDigits}`
      }
    }

    setValues((prev) => ({
      ...prev,
      [id]: processedValue,
    }))

    setValue(id, processedValue)

    if (id in errors && errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: '',
      }))
    }

    if (storeErrors && Array.isArray(storeErrors) && storeErrors.length > 0) {
      clearErrors()
    }
  }

  const validateForm = (): boolean => {
    const newErrors: RegistrationErrors = {}
    let isValid = true

    if (!values.jobPosition.trim()) {
      newErrors.jobPosition = 'Job position is required'
      isValid = false
    } else if (values.jobPosition.trim().length < 2) {
      newErrors.jobPosition = 'Job position must be at least 2 characters'
      isValid = false
    } else if (values.jobPosition.trim().length > 50) {
      newErrors.jobPosition = 'Job position must not exceed 50 characters'
      isValid = false
    }

    const phoneRegex = /^\+?[0-9]{6,15}$/
    if (!values.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required'
      isValid = false
    } else if (!phoneRegex.test(values.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must be 6-15 digits'
      isValid = false
    }

    if (!values.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
      isValid = false
    } else if (values.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters'
      isValid = false
    } else if (values.companyName.trim().length > 50) {
      newErrors.companyName = 'Company name must not exceed 50 characters'
      isValid = false
    }

    if (!values.region) {
      newErrors.region = 'Please select a region'
      isValid = false
    }

    if (!values.industries || values.industries.length === 0) {
      newErrors.industries = 'Please select at least one industry'
      isValid = false
    }

    if (values.industries && values.industries.length > 3) {
      newErrors.industries = 'You can select a maximum of 3 industries'
      isValid = false
    }

    if (values.annualBudget === '') {
      newErrors.annualBudget = 'Please select annual budget'
      isValid = false
    }

    if (!values.companySize) {
      newErrors.companySize = 'Please select company size'
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  const handleSubmit = async (): Promise<void> => {
    try {
      if (!validateForm()) {
        notificationService.error('Please check the form for errors')
        return
      }

      const brandUserId = brandUser?.id
      const stateId = id
      const formUserId = values.userId
      const brandDataId = useAccessStore.getState().brandData?.id

      let userId = brandDataId || brandUserId || formUserId || stateId

      if (!userId) {
        try {
          const brandData = await useAccessStore.getState().fetchBrandData()
          if (brandData?.id) {
            userId = brandData.id

            setValue('id', userId)
            setValues((prev) => ({
              ...prev,
              userId,
            }))
          } else {
            logError('Brand data returned without ID field', { brandData })
          }
        } catch (error) {
          logError('Failed to fetch brand data for form submission', { error })
        }
      }

      if (!userId) {
        logError('Missing user ID when submitting form', {
          brandUserId,
          formUserId,
          stateId,
          brandDataId,
        })
        notificationService.error('Error with user ID. Please start registration again.')
        return
      }

      if (values.userId !== userId && formUserId !== userId) {
        setValue('id', userId)
        setValues((prev) => ({
          ...prev,
          userId,
        }))
      }

      const result = await registrationStepTwo(false)

      if (result) {
        const status = await checkRegistrationStatus()
        notificationService.success('Your profile has been updated successfully!')

        const allFieldsFilled =
          !!brandUser?.jobPosition &&
          !!brandUser?.phoneNumber &&
          !!brandUser?.companyName &&
          !!brandUser?.region &&
          !!brandUser?.industries &&
          brandUser?.industries.length > 0 &&
          !!brandUser?.annualBudget &&
          !!brandUser?.companySize

        if (status === 'completed' && allFieldsFilled) {
          navigate('/campaigns', { replace: true })
        } else if (!allFieldsFilled) {
          notificationService.info('Please fill in all required fields to access the project.')
        }
      }
    } catch (error) {
      logError('Error submitting registration step two', { error })
      console.error('Registration step 2 error:', error)

      const axiosError = error as {
        response?: {
          data?: {
            data?: Record<string, string>
            message?: string
            [key: string]: unknown
          }
        }
        status?: number
        message?: string
      }

      const parsedErrors = ErrorHandler.parseApiErrorResponse(axiosError.response?.data)

      if (Object.keys(parsedErrors).length > 0) {
        let errorsToSet = parsedErrors

        if (parsedErrors.general && Object.keys(parsedErrors).length > 1) {
          const { general, ...rest } = parsedErrors
          errorsToSet = rest
        }

        setErrors(errorsToSet as RegistrationErrors)

        const firstError = Object.values(errorsToSet)[0]
        notificationService.error(firstError || 'Please correct the errors in the form')
      } else {
        const defaultMessage = 'An error occurred while saving data'
        const responseMessage = axiosError.response?.data?.message
        const axiosMessage = axiosError.message

        const errorMessage =
          typeof responseMessage === 'string' ? responseMessage : typeof axiosMessage === 'string' ? axiosMessage : defaultMessage

        setErrors({ general: errorMessage } as RegistrationErrors)
        notificationService.error(errorMessage)
      }
    }
  }

  useEffect(() => {
    if (storeErrors && Array.isArray(storeErrors) && storeErrors.length > 0) {
      const newErrors: RegistrationErrors = {}

      for (const error of storeErrors) {
        if (error.includes("'")) {
          const match = error.match(/'([^']+)' must be between (\d+) and (\d+) characters/)
          if (match) {
            const [_, field] = match
            const frontendKey =
              field === 'Phone Number'
                ? 'phoneNumber'
                : field === 'Company Name'
                  ? 'companyName'
                  : field === 'Job Position'
                    ? 'jobPosition'
                    : field.toLowerCase().replace(/\s+/g, '')

            if (frontendKey) {
              newErrors[frontendKey as keyof RegistrationErrors] = error
            }
          }
        } else {
          newErrors.general = error
        }
      }

      setErrors((prev) => ({
        ...prev,
        ...newErrors,
      }))
    }
  }, [storeErrors])

  useEffect(() => {
    const allFieldsFilled =
      !!brandUser?.jobPosition &&
      !!brandUser?.phoneNumber &&
      !!brandUser?.companyName &&
      !!brandUser?.region &&
      !!brandUser?.industries &&
      brandUser?.industries.length > 0 &&
      !!brandUser?.annualBudget &&
      !!brandUser?.companySize

    if (registrationStatus && registrationStatus !== 'incomplete' && allFieldsFilled) {
      navigate('/campaigns', { replace: true })
    }
  }, [registrationStatus, navigate, brandUser])

  if (authStatus.isChecking) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p className={styles.loadingText}>Checking authentication...</p>
      </div>
    )
  }

  if (!authStatus.isAuthenticated) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <h2 className={styles.errorTitle}>Authentication Error</h2>
        <p className={styles.errorText}>You need to be authenticated to continue with registration. Please go back and try again.</p>
        <button type="button" className={styles.backBtn} onClick={onPrev}>
          Go Back
        </button>
      </div>
    )
  }

  return (
    <div className={styles.mainContentWrapper}>
      <h1 className={styles.title}>Company Information</h1>
      <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
        <div className={inputStyles.errorTextWrapper}>
          {errors.general && (
            <>
              <img src={errorIcon} alt="error" />
              <p className={inputStyles.errorText}>{errors.general}</p>
            </>
          )}
        </div>

        <label className={inputStyles.label} htmlFor="companyName">
          Company Name
        </label>
        <input
          className={`${inputStyles.input} ${errors.companyName ? inputStyles.inputError : ''}`}
          id="companyName"
          type="text"
          placeholder="Company name"
          value={values.companyName}
          onChange={handleChange}
          disabled={isLoading}
        />
        <div className={inputStyles.errorTextWrapper}>
          {errors.companyName && (
            <>
              <img src={errorIcon} alt="error" />
              <p className={inputStyles.errorText}>{errors.companyName}</p>
            </>
          )}
        </div>

        <div className={inputStyles.doubleInputs}>
          <div className={inputStyles.inputWrapper}>
            <label className={inputStyles.label} htmlFor="jobPosition">
              Job Position
            </label>
            <input
              className={`${inputStyles.input} ${errors.jobPosition ? inputStyles.inputError : ''}`}
              id="jobPosition"
              type="text"
              placeholder="Your position"
              value={values.jobPosition}
              onChange={handleChange}
              disabled={isLoading}
            />
            <div className={inputStyles.errorTextWrapper}>
              {errors.jobPosition && (
                <>
                  <img src={errorIcon} alt="error" />
                  <p className={inputStyles.errorText}>{errors.jobPosition}</p>
                </>
              )}
            </div>
          </div>
          <div className={inputStyles.inputWrapper}>
            <label className={inputStyles.label} htmlFor="phoneNumber">
              Phone Number
            </label>
            <input
              className={`${inputStyles.input} ${errors.phoneNumber ? inputStyles.inputError : ''}`}
              id="phoneNumber"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Phone number"
              value={values.phoneNumber}
              onChange={handleChange}
              disabled={isLoading}
            />
            <div className={inputStyles.errorTextWrapper}>
              {errors.phoneNumber && (
                <>
                  <img src={errorIcon} alt="error" />
                  <p className={inputStyles.errorText}>{errors.phoneNumber}</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={inputStyles.doubleInputs}>
          <div className={inputStyles.inputWrapper}>
            <label className={inputStyles.label} htmlFor="region">
              Region
            </label>
            <select
              className={`${inputStyles.input} ${errors.region ? inputStyles.inputError : ''} ${optionsLoading.regions ? styles.loading : ''}`}
              id="region"
              value={values.region}
              onChange={handleChange}
              disabled={optionsLoading.regions || isLoading}
            >
              <option value="" disabled hidden>
                Select Region
              </option>
              {optionsData.regions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className={inputStyles.errorTextWrapper}>
              {errors.region && <img src={errorIcon} alt="error" />}
              <p className={inputStyles.errorText}>{errors.region}</p>
            </div>
          </div>
          <div className={inputStyles.inputWrapper}>
            <label className={inputStyles.label} htmlFor="industries">
              Industries (max 3)
            </label>
            <div className={styles.industriesContainer}>
              {optionsLoading.industries ? (
                <div className={styles.loadingText}>Loading...</div>
              ) : (
                <div className={styles.industriesList}>
                  {optionsData.industries.map((option) => {
                    const isSelected = values.industries.includes(option.value)
                    return (
                      <button
                        type="button"
                        key={option.value}
                        className={`${styles.industryItem} ${isSelected ? styles.industryItemSelected : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setValues((prev) => ({
                              ...prev,
                              industries: prev.industries.filter((i) => i !== option.value),
                            }))
                            setValue(
                              'industries',
                              values.industries.filter((i) => i !== option.value),
                            )
                          } else if (values.industries.length < 3) {
                            setValues((prev) => ({
                              ...prev,
                              industries: [...prev.industries, option.value],
                            }))
                            setValue('industries', [...values.industries, option.value])
                          }

                          setErrors((prev) => ({
                            ...prev,
                            industries: undefined,
                          }))
                        }}
                        aria-pressed={isSelected}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            <div className={inputStyles.errorTextWrapper}>
              {errors.industries && <img src={errorIcon} alt="error" />}
              <p className={inputStyles.errorText}>{errors.industries}</p>
            </div>
          </div>
        </div>

        <div className={inputStyles.doubleInputs}>
          <div className={inputStyles.inputWrapper}>
            <label className={inputStyles.label} htmlFor="annualBudget">
              Annual Marketing Budget
            </label>
            <select
              className={`${inputStyles.input} ${errors.annualBudget ? inputStyles.inputError : ''}`}
              id="annualBudget"
              value={values.annualBudget}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value="" disabled hidden>
                Select Annual Budget
              </option>
              {optionsData.budgets.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className={inputStyles.errorTextWrapper}>
              {errors.annualBudget && <img src={errorIcon} alt="error" />}
              <p className={inputStyles.errorText}>{errors.annualBudget}</p>
            </div>
          </div>
          <div className={inputStyles.inputWrapper}>
            <label className={inputStyles.label} htmlFor="companySize">
              Company Size
            </label>
            <select
              className={`${inputStyles.input} ${errors.companySize ? inputStyles.inputError : ''}`}
              id="companySize"
              value={values.companySize}
              onChange={handleChange}
              disabled={isLoading}
            >
              <option value="" disabled hidden>
                Select Company Size
              </option>
              {optionsData.companySizes.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className={inputStyles.errorTextWrapper}>
              {errors.companySize && <img src={errorIcon} alt="error" />}
              <p className={inputStyles.errorText}>{errors.companySize}</p>
            </div>
          </div>
        </div>

        <label className={inputStyles.label} htmlFor="brandLogo">
          Brand Logo
        </label>
        <div className={styles.fileUploadContainer}>
          <input type="file" id="brandLogo" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: 'none' }} />
          <div className={styles.fileUploadWrapper}>
            <button
              type="button"
              onClick={handleBrowseClick}
              className={`${styles.uploadBtn} ${isUploading ? styles.uploadBtnDisabled : ''}`}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Choose Logo File'}
            </button>
            <div className={styles.fileNameDisplay}>
              {selectedFile ? selectedFile.name : 'No file selected'}
              {values.brandLogoUrl && !selectedFile && <span className={styles.uploadedStatus}>(Logo already uploaded)</span>}
            </div>
          </div>
          <div className={inputStyles.errorTextWrapper}>
            {errors.brandLogoUrl && (
              <>
                <img src={errorIcon} alt="error" />
                <p className={inputStyles.errorText}>{errors.brandLogoUrl}</p>
              </>
            )}
          </div>
        </div>

        <label className={inputStyles.label} htmlFor="additionalInformation">
          Additional Information (Optional)
        </label>
        <textarea
          className={`${inputStyles.input} ${styles.textarea}`}
          id="additionalInformation"
          placeholder="Any additional information you'd like to share"
          value={values.additionalInformation}
          onChange={handleChange}
          rows={4}
          disabled={isLoading}
        />

        <div className={styles.buttonGroup}>
          <button
            type="button"
            className={`${styles.submitBtn} ${isLoading ? styles.submitBtnDisabled : ''}`}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Complete Registration'}
          </button>
        </div>
      </form>
    </div>
  )
}
